import { Box, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import {
  IconIllustrationSmallChildCare,
  IconIllustrationSmallSafetyCheck,
  IconIllustrationSmallSeniorCare,
  IconIllustrationSmallPolaroids,
  IconIllustrationSmallHeart,
} from '@care/react-icons';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess_caregiverStats as SEOSegmentationCaregiverStats } from '@/__generated__/seoSegmentationListingPage';
import { ServiceIdForMember } from '@/constants';
import { SxClassProps } from '@/types/common';
import { usePageAttributes } from './contexts/PageAttributesContext';

const classes: SxClassProps = {
  brandDeviceIcon: (theme) => ({
    width: '65px',
    height: '65px',
    marginTop: theme.spacing(-1.5),
    marginLeft: theme.spacing(-0.6),
  }),
  stepIcon: (theme) => ({
    width: '50px',
    height: '50px',
    marginRight: 3,
    textAlign: 'center',
    alignSelf: 'flex-start',
    paddingTop: 1,
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  }),
  stat: (theme) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 1,
    width: '285px',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      width: '350px',
    },

    '& div:first-of-type': {
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto',
      overflow: 'visible',
      textAlign: 'center',
      fontWeight: 700,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        height: '50px',
      },
    },
  }),
  statDescription: (theme) => ({
    fontSize: theme.typography.h5.fontSize,
    padding: theme.spacing(0, 0, 0, 1),
    color: theme.palette.care.grey[700],

    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
      padding: theme.spacing(1, 5),
      fontSize: '1rem',
    },
  }),
};

interface Props {
  avgPayRate: SEOSegmentationCaregiverStats['avgPayRate'];
  avgYearsOfExperience: SEOSegmentationCaregiverStats['avgYearsOfExperience'];
  avgReviewRating: SEOSegmentationCaregiverStats['avgReviewRating'];
  totalProviders: SEOSegmentationCaregiverStats['totalProviders'];
  slotProps?: {
    contentRibbon?: {
      'data-testid': string;
    };
  };
}

const ContentRibbon = ({
  avgPayRate,
  avgYearsOfExperience,
  avgReviewRating,
  totalProviders,
  slotProps,
}: Props) => {
  const { pageType, segmentNamePluralLowerCase, city, state, serviceIdForMember } =
    usePageAttributes();
  const isMobleOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const currentDate = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date()
  );

  const roundAvgReviewRating = avgReviewRating ? avgReviewRating.toFixed(1) : null;
  const roundAvgYearsOfExp = avgYearsOfExperience ? Math.floor(avgYearsOfExperience) : null;

  const statForReviewRatingOrYearsOfExp =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `${roundAvgReviewRating}`
      : `${roundAvgYearsOfExp}`;
  const ribbonIconOne =
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION ? (
      <IconIllustrationSmallChildCare sx={classes.brandDeviceIcon} />
    ) : (
      <IconIllustrationSmallPolaroids sx={classes.brandDeviceIcon} />
    );

  const ribbonIconThree =
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION ? (
      <IconIllustrationSmallHeart sx={classes.brandDeviceIcon} />
    ) : (
      <IconIllustrationSmallSeniorCare sx={classes.brandDeviceIcon} />
    );

  const headline =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `Find ${segmentNamePluralLowerCase} that you’ll love`
      : `Find ${segmentNamePluralLowerCase} in ${city},&nbsp;${state} that you’ll love`;

  const descriptionForTotalProviders =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `${totalProviders?.toLocaleString()} ${segmentNamePluralLowerCase} are listed on Care.com`
      : `${totalProviders?.toLocaleString()} ${segmentNamePluralLowerCase} are listed in ${city},&nbsp;${state}`;

  const descriptionForAvgPayRate = `The average posted rate is $${avgPayRate?.amount}/hr as of ${currentDate}`;
  const descriptionForReviewRatingOrYearsOfExp =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `The average star rating for rated ${segmentNamePluralLowerCase} is ${statForReviewRatingOrYearsOfExp}`
      : `The average experience for nearby ${segmentNamePluralLowerCase} is ${statForReviewRatingOrYearsOfExp} years`;

  return (
    <Grid item xs={12} bgcolor="care.grey.50" pb={2.5} mb={2} {...slotProps?.contentRibbon}>
      <Typography
        component="h2"
        variant={isMobleOrSmaller ? 'h3' : 'h2'}
        textAlign="center"
        padding={(theme) => ({ xs: theme.spacing(2, 6, 2, 6), md: theme.spacing(3, 6, 2, 6) })}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(he.decode(headline)),
        }}
      />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 15%',

          [theme.breakpoints.only('sm')]: {
            padding: '0 30%',
          },

          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
          },

          [theme.breakpoints.up('lg')]: {
            padding: '0 15%',
          },
        })}>
        <Box sx={classes.stat}>
          <Box sx={classes.stepIcon}>{ribbonIconOne}</Box>
          <Typography
            sx={classes.statDescription}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(he.decode(descriptionForTotalProviders)),
            }}
          />
        </Box>
        <Box sx={classes.stat}>
          <Box sx={classes.stepIcon}>
            <IconIllustrationSmallSafetyCheck sx={classes.brandDeviceIcon} />
          </Box>
          <Typography sx={classes.statDescription}>{descriptionForAvgPayRate}</Typography>
        </Box>
        <Box sx={classes.stat}>
          <Box sx={classes.stepIcon}>{ribbonIconThree}</Box>
          <Typography sx={classes.statDescription}>
            {descriptionForReviewRatingOrYearsOfExp}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default ContentRibbon;
